import { Link, Navigate, useLocation } from "react-router";
import { Aviso, Cartao } from "../ui";
import { Casca } from "../layout";
import { MarcaGeovendas } from "../layout/MarcaGeovendas";
import { ErroDaApi } from "../../nucleo/api";
import { ChatDoCliente } from "../webchat/ChatDoCliente";
import { descricaoDoUsuario, ehInterno, podeNaSessao, useSair, useSessao } from "../../nucleo/sessao";

export interface PropsDaRotaProtegida {
  /** Nome da área mostrado na topbar. */
  area: string;
  lado: "cliente" | "interna";
  /** Fecha a rota para quem não é do time interno da GEOvendas. */
  exigeInterno?: boolean;
}

function TelaDeEspera() {
  return (
    <div className="tela-de-espera">
      <MarcaGeovendas />
      <p className="tela-de-espera__texto" role="status">
        Verificando o seu acesso…
      </p>
    </div>
  );
}

/**
 * Porta de todas as rotas com sessão.
 *
 * Fail-closed em três situações que é fácil confundir: sem sessão, com a consulta ainda
 * em andamento e com a consulta falhando. Só o primeiro caso é "não entrou"; os outros dois
 * são "não sabemos" — e nenhum deles abre a rota. A falha na consulta também manda para a
 * entrada, porque a alternativa (mostrar a tela e deixar cada requisição falhar depois) faz
 * a pessoa acreditar que está dentro.
 */
export function RotaProtegida({ area, lado, exigeInterno = false }: PropsDaRotaProtegida) {
  const { pathname, search } = useLocation();
  const sessao = useSessao();
  const sair = useSair();

  if (sessao.isPending) return <TelaDeEspera />;

  if (sessao.isError || !sessao.data) {
    // O destino pretendido viaja no estado da navegação, não na URL: nada do que a pessoa
    // tentou abrir vira parâmetro visível, e não há como induzir um redirecionamento externo.
    // A rede da organização pode recusar o endereço de quem já tinha sessão: a frase é escrita
    // para a pessoa saber o que fazer, e viaja no estado até a tela de entrada mostrá-la.
    const motivo =
      sessao.error instanceof ErroDaApi && sessao.error.codigo === "ip_nao_permitido"
        ? sessao.error.message
        : undefined;
    return (
      <Navigate to="/entrar" replace state={{ de: `${pathname}${search}`, ...(motivo ? { motivo } : {}) }} />
    );
  }

  const usuario = {
    nome: sessao.data.usuario.nome,
    papel: descricaoDoUsuario(sessao.data),
  };

  if (exigeInterno && !ehInterno(sessao.data)) {
    return (
      <Casca area={area} lado="cliente" usuario={usuario} aoSair={() => sair.mutate()}>
        <h1 className="titulo-pagina">Área restrita ao time da GEOvendas</h1>
        <p className="lead">
          Esta parte do portal é do backoffice interno. A sua conta está ativa, mas não tem
          o papel que abre esta área.
        </p>
        <Cartao>
          <Aviso tom="atencao">
            <b>Nada foi carregado.</b> A negativa acontece antes de qualquer consulta —
            nenhum dado desta área foi lido para chegar a esta tela.
          </Aviso>
          <p className="cartao__texto mt-4">
            Se você precisa deste acesso, peça ao administrador da sua organização ou ao
            consultor responsável pela implantação.
          </p>
          <div className="mt-4">
            <Link to="/app" className="botao botao--fantasma">
              Voltar para a minha área
            </Link>
          </div>
        </Cartao>
      </Casca>
    );
  }

  return (
    <>
      <Casca area={area} lado={lado} usuario={usuario} aoSair={() => sair.mutate()} />
      {/* O chat com a equipe segue o cliente por todas as telas da área dele. */}
      {lado === "cliente" && podeNaSessao(sessao.data, "webchat:usar") ? <ChatDoCliente /> : null}
    </>
  );
}
